<script setup lang="ts">
import { computed, ref } from "vue";
import { BookOpen, ExternalLink } from "@lucide/vue";
import type { DocSource } from "../types/chatTypes";
import { isSafeDocSourceUrl } from "../utils/docSourceUrl";

// How many chips to show before collapsing the rest behind a "+N more" toggle. The backend doesn't
// cap the list, and a long relevance-ordered tail would otherwise push the answer off screen in the
// narrow chat panel.
const COLLAPSED_LIMIT = 3;

const props = defineProps<{
  sources: DocSource[];
}>();

const expanded = ref(false);

// Second gate on the same rule the parser applies: a url reaching an href must be http(s), whatever
// the caller passed in. A chip with no safe destination is dropped rather than rendered inert.
const safeSources = computed(() => props.sources.filter((source) => isSafeDocSourceUrl(source.url)));

const hiddenCount = computed(() => Math.max(safeSources.value.length - COLLAPSED_LIMIT, 0));
const visibleSources = computed(() => (expanded.value ? safeSources.value : safeSources.value.slice(0, COLLAPSED_LIMIT)));
</script>

<template>
  <div
    v-if="safeSources.length > 0"
    class="gap-px-6 mb-px-8 flex max-w-[85%] flex-col">
    <div class="gap-px-6 chat-text-muted flex items-center">
      <BookOpen
        class="icon-xs shrink-0"
        :stroke-width="1.5"
        aria-hidden="true" />
      <span class="t-footnote">Sources</span>
    </div>

    <ul class="gap-px-6 flex flex-wrap items-center">
      <li
        v-for="source in visibleSources"
        :key="source.url">
        <a
          :href="source.url"
          target="_blank"
          rel="noopener noreferrer"
          :title="source.title"
          class="chat-button-filled gap-px-6 px-px-10 py-px-4 t-caption focus-ring duration-fast ease-standard flex max-w-[240px] items-center rounded-full transition-colors">
          <span class="truncate">{{ source.title }}</span>
          <ExternalLink
            class="icon-xs shrink-0"
            :stroke-width="1.5"
            aria-hidden="true" />
        </a>
      </li>

      <li v-if="hiddenCount > 0 && !expanded">
        <button
          type="button"
          class="chat-button-subtle px-px-10 py-px-4 t-caption focus-ring interactive-active duration-fast ease-standard rounded-full transition-colors"
          :aria-label="`Show ${hiddenCount} more source${hiddenCount === 1 ? '' : 's'}`"
          @click="expanded = true">
          +{{ hiddenCount }} more
        </button>
      </li>
    </ul>
  </div>
</template>
